import { Flex, InputLeftAddon, Link, SimpleGrid } from '@chakra-ui/react'
import { useEffect } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'

import { Card } from '@/components/Elements/'
import { Checkbox, Input, PriceInput, Select } from '@/components/Form'

import ContractInfo from './ContractInfo/ContractInfo'
import Equipments from './SLA/Equipments'
import SystemInfo from './SystemInfo/SystemInfo'

import TotalContractPriceSummary from './TotalContractPrice/TotalContractPriceSummary'

// Custom Component

import { useCostCenter } from '@/features/costCenters/useCostCenter'
import { useGCKCode } from '@/features/GCKCodes/useGCKCode'
import { OfferType } from '@/features/offers/constants'
import useOffer from '@/features/offers/hooks/useOffer'
import { createOptions } from '@/utils/arrayHelper'
import { roundNumber } from '@/utils/stringHelper'
import MaintenanceTotalContractPriceSection from './TotalContractPrice/MaintenanceTotalContractPriceSection'
import config from '@/config'
import { selectOfferDetail, selectOfferPermissions } from '@/features/offers/slices/offerDetail.slice'
import { offerQuotationGroupPolicyType } from '@/features/offers/constants/offerEditRights/offerQuotationEditRights'
import { OfferBookingStatus } from '@eapproval/offer-shared'

const SubSystemForm = ({ index }) => {
  // Redux State
  const { adminMode } = useSelector(selectOfferDetail)
  const offerPermissions = useSelector(selectOfferPermissions)

  // Local State

  const { useFetchAllCostCenters, useCostCenterOptions } = useCostCenter()
  const { useFetchAllGCKCodes, useGCKCodeOptions } = useGCKCode()
  const { useFetchAllOffers } = useOffer()

  const { setValue, getValues } = useFormContext()

  const offerId = useWatch({ name: '_id' })
  const type = useWatch({ name: `subSystems.${index}.type` })
  const profitCenterId = useWatch({
    name: `subSystems.${index}.profitCenter._id`,
  })
  const newOrRenewal = useWatch({ name: `subSystems.${index}.newOrRenewal` })
  const previousOffer = useWatch({ name: `subSystems.${index}.previousOffer` })
  const equipments = useWatch({ name: `subSystems.${index}.SLA.equipments` })

  const canEditSubSystem = offerPermissions[offerQuotationGroupPolicyType.QUOTATION_SUB_SYSTEM]?.edit ?? false

  // Hooks

  const { data: fetchOfferResponse } = useFetchAllOffers({
    page: 1,
    pageSize: 0,
    filters: {
      mainSubSystemType: OfferType.MAINTENANCE,
      status: OfferBookingStatus.BOOKING_COMPLETED,
    },
  }) // All Offers

  //Translated
  const validOffer = Array.isArray(fetchOfferResponse?.data)
    ? fetchOfferResponse.data.filter((offer) => offer._id !== offerId)
    : []

  const previousQuotationNumberOptions = createOptions(
    validOffer,
    (offer) => `${offer.quotationNumber || '(Missing Quo#)'} - ${offer.projectName || '(Missing Name)'}`,
    '_id'
  )

  const { data: profitCenterOptions } = useCostCenterOptions({
    status: 'active',
    labelType: 'detailed',
    valueKey: '_id',
  })

  const { data: GCKOptions } = useGCKCodeOptions({
    status: 'active',
    profitCenterId: profitCenterId,
    valueKey: '_id',
    withNilOptions: true,
  })

  useEffect(() => {
    //Load Default Equipments Value
    if (Array.isArray(fetchOfferResponse?.data) && previousOffer && previousOffer !== '' && equipments.length === 0) {
      const selectedOffer = fetchOfferResponse.data.find((offer) => offer._id === previousOffer)
      if (!selectedOffer?.equipmentIds) {
        return
      }

      setValue(`subSystems.${index}.SLA.equipments`, selectedOffer.equipmentIds, {
        shouldDirty: true,
      })
    }
    //eslint-disable-next-line
  }, [index, previousOffer, setValue, equipments])

  useEffect(() => {
    const newContractPrice = getValues(`subSystems.${index}.totalContractPrice.newContractRows`)
    if (newContractPrice.length === 0) {
      return
    }

    if (newOrRenewal === 'new') {
      //Update first row
      setValue(`subSystems.${index}.totalContractPrice.newContractRows.0.averageIncrement`, 0, {
        shouldDirty: true,
      })
    } else if (newOrRenewal === 'renewal') {
      const lastMonthPrice = getValues(`subSystems.${index}.totalContractPrice.currentContract.lastMonthPrice`)
      const monthlyPrice = getValues(`subSystems.${index}.totalContractPrice.newContractRows.0.monthlyPrice`)

      if (!lastMonthPrice || !monthlyPrice) {
        return
      }
      const averageIncrement = roundNumber(((parseFloat(monthlyPrice) - lastMonthPrice) / lastMonthPrice) * 100, 2) || 0
      setValue(`subSystems.${index}.totalContractPrice.newContractRows.0.averageIncrement`, averageIncrement, {
        shouldDirty: true,
      })
    }

    //Update SubContract
  }, [getValues, index, newOrRenewal, setValue])

  return (
    <>
      <Card title={`Sub-System ${index + 1}`} hasBorder={false}>
        <SimpleGrid columns={[2, null, 2]} spacingX="40px" spacingY="15px">
          <Input
            name={`subSystems.${index}.projectName`}
            label="Name"
            type="text"
            placeholder="Enter value"
            disabled={!canEditSubSystem}
            isRow
          />

          <Select
            name={`subSystems.${index}.type`}
            label="Type"
            disabled={!adminMode}
            options={[
              { value: 'maintenance', label: 'Maintenance' },
              { value: 'project', label: 'Project' },
            ]}
            isRow
          />
          <Select
            name={`subSystems.${index}.profitCenter._id`}
            label="Profit Center"
            disabled={!adminMode}
            options={profitCenterOptions}
            isRow
          />
          <Select
            name={`subSystems.${index}.GCKCode`}
            label="GCK Code"
            disabled={!canEditSubSystem}
            isRequired={true}
            options={GCKOptions}
            isRow
          />

          <Checkbox
            name={`subSystems.${index}.isHighRisk`}
            label="Is High Risk"
            isRequired={false}
            isRow
            labelWidth={60}
            disabled={!canEditSubSystem}
          />

          <Select
            name={`subSystems.${index}.technology`}
            label="Standard Technology"
            disabled={!canEditSubSystem}
            isRequired={false}
            options={[
              { value: 'no', label: 'No' },
              { value: 'yes', label: 'Yes' },
            ]}
            isRow
          />
        </SimpleGrid>
      </Card>

      {type === 'maintenance' && (
        <Card title={'Contract Info'} hasBorder={false}>
          <SimpleGrid columns={[2, null, 2]} spacingX="40px" spacingY="15px">
            <Select
              name={`subSystems.${index}.newOrRenewal`}
              label="New/Renewal"
              disabled={!canEditSubSystem}
              options={[
                { value: 'new', label: 'New' },
                { value: 'renewal', label: 'Renewal' },
                {
                  value: 'renewalOfDiscontinued',
                  label: 'Renewal Of Discontinued',
                },
              ]}
              isRow
            />
            {newOrRenewal === 'new' && (
              <>
                <Input
                  name={`subSystems.${index}.conversionRate`}
                  label="Conversion Rate (%)"
                  type="number"
                  placeholder="Enter value"
                  disabled={!canEditSubSystem}
                  isRow
                />
                <Input
                  name={`subSystems.${index}.projectWBS`}
                  label="Project WBS"
                  type="text"
                  placeholder="Enter value"
                  disabled={!canEditSubSystem}
                  isRow
                />
                <PriceInput
                  name={`subSystems.${index}.projectValue`}
                  label="Project Value ($)"
                  placeholder="Enter Value"
                  disabled={!canEditSubSystem}
                  isRow
                />
              </>
            )}
            {newOrRenewal === 'renewal' && (
              <>
                <Input
                  name={`subSystems.${index}.existingWBSNumber`}
                  label="Existing WBS No."
                  type="text"
                  placeholder="Enter value"
                  disabled={!canEditSubSystem}
                  isRow
                />
              </>
            )}

            {newOrRenewal === 'renewalOfDiscontinued' && (
              <>
                <Input
                  name={`subSystems.${index}.existingWBSNumber`}
                  label="Previous WBS No."
                  type="text"
                  placeholder="Enter value"
                  disabled={!canEditSubSystem}
                  isRow
                />
              </>
            )}
            {['renewal', 'renewalOfDiscontinued'].includes(newOrRenewal) && (
              <Flex gap={2} direction={'column'} alignItems={'end'}>
                <Select
                  name={`subSystems.${index}.previousOffer`}
                  label="Previous Quotation No."
                  placeholder="Enter value"
                  disabled={!canEditSubSystem}
                  options={previousQuotationNumberOptions}
                  isRow
                  isMulti
                />
                <Flex gap={4} alignItems={'end'} flexWrap={'wrap'}>
                  {previousOffer &&
                    previousOffer.length > 0 &&
                    previousOffer.map((offerId) => (
                      <Link key={offerId} color="gray" href={`${config.domain}/offers/${offerId}`} target="_blank">
                        {
                          previousQuotationNumberOptions
                            .find((option) => option.value === offerId)
                            ?.label.split(' - ')[0]
                        }
                      </Link>
                    ))}
                </Flex>
              </Flex>
            )}
          </SimpleGrid>
        </Card>
      )}

      <SystemInfo subSystemIndex={index} />

      {type && (
        <Card title="Total Contract Price" hasBorder={false}>
          {type === OfferType.PROJECT && (
            <Flex maxW="50%" mb="3" pr={3}>
              <PriceInput
                name={`subSystems.${index}.totalContractPrice.newContractRows.0.total`}
                label={`Offer Price`}
                placeholder="Enter value"
                disabled={!canEditSubSystem}
                leadingIcon={<InputLeftAddon>$</InputLeftAddon>}
                isRow
              />
            </Flex>
          )}

          {type === OfferType.MAINTENANCE && <MaintenanceTotalContractPriceSection subSystemIndex={index} />}
          <TotalContractPriceSummary
            subSystemIndex={index}
            contractPriceDataPath={`subSystems.${index}.totalContractPrice`}
          />
        </Card>
      )}

      {type === 'maintenance' && <ContractInfo subSystemIndex={index} />}
      {type === 'maintenance' && <Equipments subSystemIndex={index} />}
    </>
  )
}

export default SubSystemForm
